import axios from "@/utils/axios";
import { TwoFactorStatus } from "@/types/auth";
import { components } from "./openapi";
import {
	CreateAdminRequest,
	CreateAdminResponse,
	CreateAgentReportRequest,
	CreateAgentReportResponse,
	CreateAgentRequest,
	CreateAgentResponse,
	CreateOperatorsRequest,
	CreateOperatorsResponse,
	CreateQrCodeRequest,
	CreateQrCodeResponse,
	DeleteAdminResponse,
	GetAdminBuyQuotaActivityLogRequest,
	GetAdminBuyQuotaActivityLogResponse,
	GetAdminsMeResponse,
	GetAgentByIdResponse,
	GetAgentOperatorOrderSummaryRequest,
	GetAgentOperatorOrderSummaryResponse,
	GetAgentOperatorsRequest,
	GetAgentOperatorsResponse,
	GetAgentReportRequest,
	GetAgentReportResponse,
	GetAgentsResponse,
	GetAgentSubAgentOrderSummaryRequest,
	GetAgentSubAgentOrderSummaryResponse,
	GetAgentSubAgentsRequest,
	GetAgentSubAgentsResponse,
	GetAgentsWithWinningQuotaRequest,
	GetAgentsWithWinningQuotaResponse,
	GetAgentWinningQuotaRequest,
	GetAgentWinningQuotaResponse,
	GetOperatorByIdResponse,
	GetOperatorGameSettingsByGameIdResponse,
	GetOperatorGameSettingsResponse,
	GetOperatorPoolsResponse,
	GetOperatorPresetsByPresetIdResponse,
	GetOperatorPresetsResponse,
	GetOperatorReportsResponse,
	GetOperatorSpinOrderByUsernameRequest,
	GetOperatorSpinOrderByUsernameResponse,
	GetOperatorSpinOrderSummaryByUsernamesRequest,
	GetOperatorSpinOrderSummaryByUsernamesResponse,
	GetOperatorsResponse,
	GetOperatorWinningQuotaRequest,
	GetOperatorWinningQuotaResponse,
	GetRootAgentsRequest,
	GetRootAgentsResponse,
	GetSubAgentReportRequest,
	GetSubAgentReportResponse,
	ResetOperatorPrivateKeyResponse,
	ResetOperatorQuotaRequest,
	SetOperatorGameSettingRequest,
	SetOperatorGameSettingResponse,
	SetOperatorPresetRequest,
	SetOperatorPresetResponse,
	UpdateAdminRequest,
	UpdateAdminResponse,
	UpdateAgentReportRequest,
	UpdateAgentReportResponse,
	UpdateAgentRequest,
	UpdateAgentResponse,
	UpdateOperatorRequest,
	UpdateOperatorResponse,
} from "@/types/admin";
import {
	ChangeOperatorCurrencyRequest,
	FxRate,
	GetFxRateRequest,
	OperatorCurrencyChangePreview,
} from "@/types/currency";

export const getAdminsMe = async (): Promise<GetAdminsMeResponse> => {
	try {
		const response = await axios.get(`/admins/me`);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getAdmins = async (): Promise<GetAdminsMeResponse> => {
	try {
		const response = await axios.get(`/admins`);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getImpersonateAgentToken = async (
	agentId: string,
): Promise<{ accessToken: string; role: string }> => {
	const response = await axios.post(`/admins/impersonate-agent/${agentId}`);
	return response.data;
};

export const getImpersonateOperatorToken = async (
	operatorId: string,
): Promise<{ accessToken: string; role: string }> => {
	const response = await axios.post(
		`/admins/impersonate-operator/${operatorId}`,
	);
	return response.data;
};

export const createAdmin = async (
	payload: CreateAdminRequest,
): Promise<CreateAdminResponse> => {
	try {
		const response = await axios.post(`/admins`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const updateAdmin = async (
	id: string,
	payload: UpdateAdminRequest,
): Promise<UpdateAdminResponse> => {
	try {
		const response = await axios.put(`/admins/${id}`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const deleteAdmin = async (id: string): Promise<DeleteAdminResponse> => {
	try {
		const response = await axios.delete(`/admins/${id}`);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getOperatorsWithWinningQuota = async (
	payload: components["schemas"]["GetOperatorsWithWinningQuotaDto"],
): Promise<components["schemas"]["GetOperatorsWithWinningQuotaResponse"]> => {
	try {
		const response = await axios.post(
			`/admins/get-operators-with-winning-quota`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getAgentsWithWinningQuota = async (
	payload: GetAgentsWithWinningQuotaRequest,
): Promise<GetAgentsWithWinningQuotaResponse> => {
	try {
		const response = await axios.post(
			`/admins/get-agents-with-winning-quota`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getOperators = async (): Promise<GetOperatorsResponse> => {
	try {
		const response = await axios.get(`/admins/operators`);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const createOperator = async (
	payload: CreateOperatorsRequest,
): Promise<CreateOperatorsResponse> => {
	try {
		const response = await axios.post(`/admins/operators`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getOperatorById = async (
	id: string,
): Promise<GetOperatorByIdResponse> => {
	try {
		const response = await axios.get(`/admins/operators/${id}`);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const updateOperator = async (
	id: string,
	payload: UpdateOperatorRequest,
): Promise<UpdateOperatorResponse> => {
	try {
		const response = await axios.put(`/admins/operators/${id}`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

// The market rate quota moves at between an Agent's THB and an Operator's currency.
export const getAdminFxRate = async (
	payload: GetFxRateRequest,
): Promise<FxRate> => {
	try {
		const response = await axios.post(`/admins/get-fx-rate`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

// What moving the Operator to another currency would do to its quota, at the market rate.
export const previewChangeOperatorCurrency = async (
	id: string,
	payload: ChangeOperatorCurrencyRequest,
): Promise<OperatorCurrencyChangePreview> => {
	try {
		const response = await axios.post(
			`/admins/operators/${id}/change-currency-preview`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

// Refused while the Operator is active, and with 409 if the market has moved since the preview.
export const changeOperatorCurrency = async (
	id: string,
	payload: ChangeOperatorCurrencyRequest,
): Promise<UpdateOperatorResponse> => {
	try {
		const response = await axios.post(
			`/admins/operators/${id}/change-currency`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const resetOperatorPrivateKey = async (
	id: string,
): Promise<ResetOperatorPrivateKeyResponse> => {
	try {
		const response = await axios.post(
			`/admins/operators/${id}/reset-private-key`,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getOperatorWinningQuota = async (
	id: string,
	payload: GetOperatorWinningQuotaRequest,
): Promise<GetOperatorWinningQuotaResponse> => {
	try {
		const response = await axios.post(`/admins/operators/${id}/winning-quota`);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getOperatorGameSettings = async (
	id: string,
): Promise<GetOperatorGameSettingsResponse> => {
	try {
		const response = await axios.post(`/admins/operators/${id}/game-settings`);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getOperatorGameSettingsByGameId = async (
	id: string,
	gameId: string,
): Promise<GetOperatorGameSettingsByGameIdResponse> => {
	try {
		const response = await axios.post(
			`/admins/operators/${id}/game-settings/${gameId}`,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getOperatorPresets = async (
	id: string,
): Promise<GetOperatorPresetsResponse> => {
	try {
		const response = await axios.post(
			`/admins/operators/${id}/operator-presets`,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getOperatorPresetByPresetId = async (
	id: string,
	presetId: string,
): Promise<GetOperatorPresetsByPresetIdResponse> => {
	try {
		const response = await axios.post(
			`/admins/operators/${id}/operator-presets/${presetId}`,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getOperatorPools = async (
	id: string,
): Promise<GetOperatorPoolsResponse> => {
	try {
		const response = await axios.post(`/admins/operators/${id}/pools`);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getOperatorReports = async (
	id: string,
): Promise<GetOperatorReportsResponse> => {
	try {
		const response = await axios.post(`/admins/operators/${id}/reports`);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getOperatorSpinOrderSummaryByUsernames = async (
	id: string,
	payload: GetOperatorSpinOrderSummaryByUsernamesRequest,
): Promise<GetOperatorSpinOrderSummaryByUsernamesResponse> => {
	try {
		const response = await axios.post(
			`/admins/operators/${id}/get-spin-order-summary-by-usernames`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getOperatorSpinOrderByUsername = async (
	id: string,
	payload: GetOperatorSpinOrderByUsernameRequest,
): Promise<GetOperatorSpinOrderByUsernameResponse> => {
	try {
		const response = await axios.post(
			`/admins/operators/${id}/get-spin-order-by-username`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const setOperatorGameSetting = async (
	id: string,
	payload: SetOperatorGameSettingRequest,
): Promise<SetOperatorGameSettingResponse> => {
	try {
		const response = await axios.post(
			`/admins/operators/${id}/set-game-setting`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const setOperatorPreset = async (
	id: string,
	payload: SetOperatorPresetRequest,
): Promise<SetOperatorPresetResponse> => {
	try {
		const response = await axios.post(
			`/admins/operators/${id}/set-operator-preset`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getAgents = async (): Promise<GetAgentsResponse> => {
	try {
		const response = await axios.get(`/admins/agents`);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const createAgent = async (
	payload: CreateAgentRequest,
): Promise<CreateAgentResponse> => {
	try {
		const response = await axios.post(`/admins/agents`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getAgentById = async (
	id: string,
): Promise<GetAgentByIdResponse> => {
	try {
		const response = await axios.get(`/admins/agents/${id}`);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const updateAgent = async (
	id: string,
	payload: UpdateAgentRequest,
): Promise<UpdateAgentResponse> => {
	try {
		const response = await axios.put(`/admins/agents/${id}`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getAgentOperators = async (
	id: string,
	payload: GetAgentOperatorsRequest,
): Promise<GetAgentOperatorsResponse> => {
	try {
		const response = await axios.post(
			`/admins/agents/${id}/operators`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getAgentSubAgents = async (
	id: string,
	payload: GetAgentSubAgentsRequest,
): Promise<GetAgentSubAgentsResponse> => {
	try {
		const response = await axios.post(
			`/admins/agents/${id}/sub-agents`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getRootAgents = async (
	payload: GetRootAgentsRequest,
): Promise<GetRootAgentsResponse> => {
	try {
		const response = await axios.post(
			`/admins/agents/get-root-agents`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getAgentOperatorOrderSummary = async (
	id: string,
	payload: GetAgentOperatorOrderSummaryRequest,
): Promise<GetAgentOperatorOrderSummaryResponse> => {
	try {
		const response = await axios.post(
			`/admins/agents/${id}/operator-order-summary`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getAgentSubAgentOrderSummary = async (
	id: string,
	payload: GetAgentSubAgentOrderSummaryRequest,
): Promise<GetAgentSubAgentOrderSummaryResponse> => {
	try {
		const response = await axios.post(
			`/admins/agents/${id}/sub-agent-order-summary`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getAgentWinningQuota = async (
	id: string,
	payload: GetAgentWinningQuotaRequest,
): Promise<GetAgentWinningQuotaResponse> => {
	try {
		const response = await axios.post(
			`/admins/agents/${id}/winning-quota`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getAgentReport = async (
	id: string,
	payload: GetAgentReportRequest,
): Promise<GetAgentReportResponse> => {
	try {
		const response = await axios.post(
			`/admins/agents/${id}/get-agent-report`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const createAgentReport = async (
	id: string,
	payload: CreateAgentReportRequest,
): Promise<CreateAgentReportResponse> => {
	try {
		const response = await axios.post(
			`/admins/agents/${id}/create-agent-report`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const updateAgentReport = async (
	id: string,
	payload: UpdateAgentReportRequest,
): Promise<UpdateAgentReportResponse> => {
	try {
		const response = await axios.post(
			`/admins/agents/${id}/update-agent-report`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getSubAgentReport = async (
	id: string,
	payload: GetSubAgentReportRequest,
): Promise<GetSubAgentReportResponse> => {
	try {
		const response = await axios.post(
			`/admins/agents/${id}/get-sub-agent-report`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const resetOperatorQuota = async (
	id: string,
	payload: ResetOperatorQuotaRequest,
): Promise<void> => {
	try {
		await axios.post(`/admins/agents/${id}/reset-operator-quota`, payload);
	} catch (error: any) {
		throw error.response.data;
	}
};

export const createQrCode = async (
	payload: CreateQrCodeRequest,
): Promise<CreateQrCodeResponse> => {
	try {
		const response = await axios.post(`/admins/create-qr-code`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getAdminBuyQuotaActivityLog = async (
	payload: GetAdminBuyQuotaActivityLogRequest,
): Promise<GetAdminBuyQuotaActivityLogResponse> => {
	try {
		const response = await axios.post(
			`/admins/get-admin-buy-quota-activity-log`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const manualCompleteBuyQuota = async (payload: {
	id: string;
}): Promise<void> => {
	try {
		await axios.post(`/admins/manual-complete-buy-quota`, payload);
	} catch (error: any) {
		throw error.response.data;
	}
};

/**
 * The lost-phone escape hatch. Enrolment is otherwise one-way: setup() refuses
 * once 2FA is on, and every generic update path strips the totp columns, so
 * without this an operator whose authenticator is gone can never sign in with
 * their own credentials again.
 *
 * `reason` is required (min 5 chars) and lands in the server log.
 */
export const resetOperatorTwoFactor = async (
	operatorId: string,
	reason: string,
): Promise<TwoFactorStatus> => {
	try {
		const response = await axios.post(
			`/admins/operators/${operatorId}/reset-2fa`,
			{ reason },
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const resetAgentTwoFactor = async (
	agentId: string,
	reason: string,
): Promise<TwoFactorStatus> => {
	try {
		const response = await axios.post(`/admins/agents/${agentId}/reset-2fa`, {
			reason,
		});
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};
